import React, { useState, useEffect, useCallback } from "react";
import { useModal } from "../../../../hooks/useModal";
import {
  X,
  Plus,
  Edit,
  Trash2,
  Save,
  Image as ImageIcon,
  FileText,
  FileSpreadsheet,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Target,
  Upload,
  Video,
  Info,
  Minus,
} from "lucide-react";
import { ApiService } from "../../../../services/api";

interface TestQuestion {
  id: string;
  content: string;
  type: "TEXT" | "IMAGE";
  options: string[];
  correctOption: string;
  metadata?: {
    imageUrl?: string;
    imageName?: string;
    imageDataUrl?: string;
    solutionVideoUrl?: string;
  };
  media?: {
    url: string;
    name: string;
  };
  solutionMedia?: {
    url: string;
    name: string;
  };
  order?: number;
  createdAt?: string;
}

interface TestQuestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  lessonId: string;
  sectionId: string;
  contentId: string;
  contentTitle: string;
}
export default function TestQuestionsModal({ isOpen, onClose, lessonId, sectionId, contentId, contentTitle }: TestQuestionsModalProps) {
  const { showAlert, showConfirm } = useModal();

  const [questions, setQuestions] = useState<TestQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<TestQuestion | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [solutionVideoUrl, setSolutionVideoUrl] = useState<string>("");
  const [solutionVideoFile, setSolutionVideoFile] = useState<File | null>(null);
  const [solutionType, setSolutionType] = useState<"vimeo" | "file">("vimeo");
  const [isInitialSetup, setIsInitialSetup] = useState(false);
  const [questionCount, setQuestionCount] = useState(5);
  const [optionCount, setOptionCount] = useState(4);
  const [expandedQuestions, setExpandedQuestions] = useState<{
    [key: string]: boolean;
  }>({});

  const [isUploadingBulk, setIsUploadingBulk] = useState(false);
  const [isUploadingAnswerKey, setIsUploadingAnswerKey] = useState(false);

  const extractVimeoVideoId = (url: string): string | null => {
    if (!url) return null;
    const vimeoRegex = /(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/;
    const match = url.match(vimeoRegex);
    return match ? match[1] : null;
  };

  const normalizeVimeoUrl = (raw: string): string => {
    if (!raw) return "";
    const hasAtPrefix = /^@+/.test(raw.trim());
    const cleaned = raw.trim().replace(/^@+/, "");
    const id = extractVimeoVideoId(cleaned);
    const normalized = id ? `https://vimeo.com/${id}` : cleaned;
    return hasAtPrefix ? `@${normalized}` : normalized;
  };

  const validateVimeoUrl = (url: string): boolean => {
    return extractVimeoVideoId(url) !== null;
  };

  const handleSolutionVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const maxSizeMb = 100;
      if (selectedFile.size > maxSizeMb * 1024 * 1024) {
        showAlert({
          title: "Hata",
          message: `Video dosyası ${maxSizeMb}MB'den küçük olmalıdır.`,
          type: "error",
        });
        return;
      }

      if (!selectedFile.type.startsWith("video/")) {
        showAlert({
          title: "Hata",
          message: "Lütfen geçerli bir video dosyası seçiniz.",
          type: "error",
        });
        return;
      }

      setSolutionVideoFile(selectedFile);
    }
  };

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ApiService.getLessonSectionContentQuestions(lessonId, sectionId, contentId);

      const rawQuestions = response?.data || response || [];
      setQuestions(rawQuestions);

      if (rawQuestions.length === 0) {
        setIsInitialSetup(true);
      } else {
        setIsInitialSetup(false);
        const initialExpanded: { [key: string]: boolean } = {};
        rawQuestions.forEach((q: any) => {
          initialExpanded[q.id] = false;
        });
        setExpandedQuestions(initialExpanded);
      }
    } catch (error) {
      console.error("Sorular yüklenirken hata:", error);
      setQuestions([]);
      setIsInitialSetup(true);
    } finally {
      setLoading(false);
    }
  }, [lessonId, sectionId, contentId]);

  useEffect(() => {
    if (isOpen) {
      fetchQuestions();
    } else {
      setIsInitialSetup(false);
      setEditingQuestion(null);
      setExpandedQuestions({});
      setFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    }
  }, [isOpen, fetchQuestions, previewUrl]);

  const handleBulkFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const maxFiles = questions.length;
    if (maxFiles <= 0) {
      await showAlert({
        title: "Uyarı",
        message: "Önce test sorularını oluşturun, ardından dosya yükleyin.",
        type: "error",
      });
      if (e.target) e.target.value = "";
      return;
    }
    if (files.length > maxFiles) {
      await showAlert({
        title: "Çok Fazla Dosya",
        message: `Bu test ${maxFiles} sorudan oluşuyor. En fazla ${maxFiles} dosya seçebilirsiniz.`,
        type: "error",
      });
      if (e.target) e.target.value = "";
      return;
    }
    try {
      setIsUploadingBulk(true);
      const resp = await ApiService.bulkUpdateLessonSectionContentQuestions(lessonId, sectionId, contentId, files);
      const message = resp?.message || "Toplu yükleme tamamlandı";
      await showAlert({
        title: "Toplu Yükleme",
        message,
        type: "success",
      });
      await fetchQuestions();
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error?.message || "Toplu yükleme sırasında bir hata oluştu.",
        type: "error",
      });
    } finally {
      setIsUploadingBulk(false);
      if (e.target) e.target.value = "";
    }
  };

  const handleAnswerKeySelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isXlsx =
      file.name.toLowerCase().endsWith(".xlsx") || file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    if (!isXlsx) {
      await showAlert({
        title: "Geçersiz Dosya",
        message: "Lütfen .xlsx uzantılı Excel dosyası yükleyiniz.",
        type: "error",
      });
      e.target.value = "";
      return;
    }

    try {
      setIsUploadingAnswerKey(true);
      const resp = await ApiService.uploadAnswerKey(lessonId, sectionId, contentId, file);
      const failures = Array.isArray(resp?.results?.failed) ? resp.results.failed : [];
      if (failures.length > 0) {
        const successCount = Array.isArray(resp?.results?.success) ? resp.results.success.length : 0;
        const failedCount = failures.length;
        const header = `${successCount} cevap başarıyla yüklendi, ${failedCount} cevap yüklenemedi.`;
        const lines = failures.map((f: any) => {
          const qn = f?.questionNumber !== undefined ? `Soru ${f.questionNumber}` : "Soru -";
          const row = typeof f?.row === "number" ? ` (Satır ${f.row})` : "";
          const reason = String(f?.reason || "Bilinmeyen hata");
          return `• ${qn}${row}: ${reason}`;
        });
        const message = `${header}\n\n${lines.join("\n\n")}`;
        await showAlert({
          title: "Uyarı",
          message,
          type: "warning",
        });
      } else {
        const message = resp?.message || "Cevap anahtarı yüklendi";
        await showAlert({ title: "Başarılı", message, type: "success" });
      }
      await fetchQuestions();
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error?.message || "Cevap anahtarı yüklenirken bir hata oluştu.",
        type: "error",
      });
    } finally {
      setIsUploadingAnswerKey(false);
      if (e.target) e.target.value = "";
    }
  };

  const createMultipleQuestions = async () => {
    setLoading(true);
    try {
      for (let i = 0; i < questionCount; i++) {
        const dynamicOptions = [] as any[];
        for (let j = 0; j < optionCount; j++) {
          const optionLetter = String.fromCharCode(65 + j);
          dynamicOptions.push(optionLetter);
        }

        const formData = new FormData();
        formData.append("type", "TEXT");
        formData.append("content", `Soru ${i + 1}`);
        formData.append("options", JSON.stringify(dynamicOptions));
        formData.append("correctOption", JSON.stringify("A"));
        formData.append("metadata", JSON.stringify({}));

        await ApiService.createLessonSectionContentQuestion(lessonId, sectionId, contentId, formData);
      }
      await fetchQuestions();
      setIsInitialSetup(false);

      await showAlert({
        title: "Başarılı",
        message: `${questionCount} adet soru başarıyla oluşturuldu.`,
        type: "success",
      });
    } catch (error) {
      await showAlert({
        title: "Hata",
        message: "Sorular oluşturulurken bir hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSingleQuestion = async () => {
    try {
      let newOptionCount = 4;
      if (questions.length > 0) {
        const totalOptions = questions.reduce((sum, q) => sum + q.options.length, 0);
        newOptionCount = Math.round(totalOptions / questions.length);
      }

      const dynamicOptions = [];
      for (let j = 0; j < newOptionCount; j++) {
        const optionLetter = String.fromCharCode(65 + j);
        dynamicOptions.push(optionLetter);
      }

      const formData = new FormData();
      formData.append("type", "TEXT");
      formData.append("content", `Soru ${questions.length + 1}`);
      formData.append("options", JSON.stringify(dynamicOptions));
      formData.append("correctOption", JSON.stringify("A"));
      formData.append("metadata", JSON.stringify({}));

      await ApiService.createLessonSectionContentQuestion(lessonId, sectionId, contentId, formData);

      await fetchQuestions();

      await showAlert({
        title: "Başarılı",
        message: "Soru başarıyla eklendi.",
        type: "success",
      });
    } catch (error) {
      await showAlert({
        title: "Hata",
        message: "Soru eklenirken bir hata oluştu.",
        type: "error",
      });
    }
  };

  const handleUpdateQuestion = async () => {
    if (!editingQuestion) return;

    if (!editingQuestion.content?.trim()) {
      showAlert({
        title: "Hata",
        message: "Lütfen soru başlığını giriniz.",
        type: "error",
      });
      return;
    }

    if (editingQuestion.type === "IMAGE" && !file && !editingQuestion.media?.url) {
      showAlert({
        title: "Hata",
        message: "Lütfen soru resmi seçiniz.",
        type: "error",
      });
      return;
    }

    if (!editingQuestion.options || editingQuestion.options.length < 2) {
      showAlert({
        title: "Hata",
        message: "En az 2 seçenek olmalıdır.",
        type: "error",
      });
      return;
    }

    if (!editingQuestion.correctOption) {
      showAlert({
        title: "Hata",
        message: "Lütfen doğru cevabı seçiniz.",
        type: "error",
      });
      return;
    }

    try {
      const questionData = {
        type: editingQuestion.type || "TEXT",
        content: editingQuestion.content || "",
        options: editingQuestion.options || [],
      } as any;

      const formData = new FormData();
      formData.append("type", questionData.type);
      formData.append("options", JSON.stringify(questionData.options));
      formData.append("correctOption", JSON.stringify(editingQuestion.correctOption || ""));
      let finalMetadata = { ...(editingQuestion.metadata || {}) };

      if (solutionType === "vimeo" && solutionVideoUrl.trim()) {
        const normalized = normalizeVimeoUrl(solutionVideoUrl);
        if (!validateVimeoUrl(normalized)) {
          showAlert({
            title: "Hata",
            message: "Geçerli bir Vimeo linki giriniz. Örnek: https://vimeo.com/123456789",
            type: "error",
          });
          return;
        }
        finalMetadata.solutionVideoUrl = normalized;
      } else {
        delete finalMetadata.solutionVideoUrl;
      }

      formData.append("metadata", JSON.stringify(finalMetadata));

      if (editingQuestion.type === "IMAGE") {
        if (file) {
          formData.append("file", file!);
        }
      }

      if (questionData.content) {
        formData.append("content", questionData.content);
      }

      if (solutionType === "file" && solutionVideoFile) {
        formData.append("solutionFile", solutionVideoFile);
      }

      await ApiService.updateLessonSectionContentQuestion(lessonId, sectionId, contentId, editingQuestion.id, formData);

      await fetchQuestions();
      setEditingQuestion(null);
      setFile(null);
      setSolutionVideoUrl("");
      setSolutionVideoFile(null);
      setSolutionType("vimeo");
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }

      await showAlert({
        title: "Başarılı",
        message: "Soru başarıyla güncellendi.",
        type: "success",
      });
    } catch (error) {
      await showAlert({
        title: "Hata",
        message: "Soru güncellenirken bir hata oluştu.",
        type: "error",
      });
    }
  };

  const handleDeleteQuestion = async (questionId: string, questionText: string) => {
    const confirmed = await showConfirm({
      title: "Soruyu Sil",
      message: `"${questionText.substring(0, 50)}..." sorusunu silmek istediğinizden emin misiniz?`,
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (!confirmed) return;

    try {
      await ApiService.deleteLessonSectionContentQuestion(lessonId, sectionId, contentId, questionId);
      await fetchQuestions();

      await showAlert({
        title: "Başarılı",
        message: "Soru başarıyla silindi.",
        type: "success",
      });
    } catch (error) {
      await showAlert({
        title: "Hata",
        message: "Soru silinirken bir hata oluştu.",
        type: "error",
      });
    }
  };

  const toggleQuestionExpansion = (questionId: string) => {
    setExpandedQuestions((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        showAlert({
          title: "Hata",
          message: "Lütfen geçerli bir resim dosyası seçiniz.",
          type: "error",
        });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showAlert({
          title: "Hata",
          message: "Resim dosyası 5MB'den küçük olmalıdır.",
          type: "error",
        });
        return;
      }

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      const newPreviewUrl = URL.createObjectURL(file);
      setFile(file);
      setPreviewUrl(newPreviewUrl);
    }
  };

  const handleOptionChange = (index: number, value: string, isEditing = false) => {
    if (isEditing && editingQuestion) {
      const oldOption = editingQuestion.options[index];
      const newOptions = [...editingQuestion.options];
      newOptions[index] = value;

      let newCorrectOption = editingQuestion.correctOption;
      if (editingQuestion.correctOption === oldOption) {
        newCorrectOption = value;
      }

      setEditingQuestion({
        ...editingQuestion,
        options: newOptions,
        correctOption: newCorrectOption,
      });
    }
  };

  const addOption = (isEditing = false) => {
    if (isEditing && editingQuestion) {
      setEditingQuestion({
        ...editingQuestion,
        options: [...editingQuestion.options, String.fromCharCode(65 + editingQuestion.options.length)],
      });
    }
  };

  const removeOption = (index: number, isEditing = false) => {
    if (isEditing && editingQuestion) {
      if (editingQuestion.options.length <= 2) return;

      const removedOption = editingQuestion.options[index];
      const newOptions = editingQuestion.options.filter((_: any, i: any) => i !== index);

      let newCorrectOption = editingQuestion.correctOption;
      if (editingQuestion.correctOption === removedOption) {
        newCorrectOption = "";
      }

      setEditingQuestion({
        ...editingQuestion,
        options: newOptions,
        correctOption: newCorrectOption,
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[95vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Test Soruları</h2>
                <p className="text-sm text-gray-600">{contentTitle}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {isInitialSetup && (
            <div className="bg-blue-50 rounded-xl p-8 text-center border border-blue-100">
              <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Target className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Test Soruları Oluştur</h3>
              <p className="text-gray-600 mb-6">
                Kaç adet soru ve her soru için kaç seçenek oluşturmak istiyorsunuz? Sonradan her soruyu düzenleyebilir ve seçenek ekleyip
                çıkarabilirsiniz.
              </p>

              <div className="max-w-xs mx-auto mb-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-center">Soru Sayısı</label>
                  <div className="flex items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => setQuestionCount((prev) => Math.max(1, prev - 1))}
                      className="w-12 h-12 flex items-center justify-center bg-white border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-blue-600 transition-all active:scale-95 disabled:opacity-50"
                      disabled={loading || questionCount <= 1}>
                      <Minus className="w-5 h-5" />
                    </button>
                    <input
                      type="number"
                      value={questionCount}
                      onChange={(e) => setQuestionCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                      className="w-24 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setQuestionCount((prev) => Math.min(50, prev + 1))}
                      className="w-12 h-12 flex items-center justify-center bg-white border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-blue-600 transition-all active:scale-95 disabled:opacity-50"
                      disabled={loading || questionCount >= 50}>
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-center">Her Soru İçin Seçenek Sayısı</label>
                  <div className="flex items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => setOptionCount((prev) => Math.max(2, prev - 1))}
                      className="w-12 h-12 flex items-center justify-center bg-white border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-blue-600 transition-all active:scale-95 disabled:opacity-50"
                      disabled={loading || optionCount <= 2}>
                      <Minus className="w-5 h-5" />
                    </button>
                    <div className="flex flex-col items-center">
                      <input
                        type="number"
                        value={optionCount}
                        onChange={(e) => setOptionCount(Math.max(2, Math.min(5, parseInt(e.target.value) || 2)))}
                        className="w-24 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        disabled={loading}
                      />
                      <span className="text-[10px] text-gray-500 mt-1 uppercase font-medium">
                        (A-{String.fromCharCode(64 + optionCount)})
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOptionCount((prev) => Math.min(5, prev + 1))}
                      className="w-12 h-12 flex items-center justify-center bg-white border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-blue-600 transition-all active:scale-95 disabled:opacity-50"
                      disabled={loading || optionCount >= 5}>
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center gap-3">
                <button onClick={onClose} className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors" disabled={loading}>
                  İptal
                </button>
                <button
                  onClick={createMultipleQuestions}
                  disabled={loading}
                  className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50">
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Oluşturuluyor...
                    </>
                  ) : (
                    <>
                      <Target className="w-4 h-4" />
                      Soruları Oluştur
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {!isInitialSetup && (
            <div className="space-y-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Toplam {questions.length} soru</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="relative">
                    <input
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      onChange={handleBulkFilesSelected}
                      className="hidden"
                      disabled={loading || isUploadingBulk || questions.length === 0}
                    />
                    <span
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm cursor-pointer ${
                        loading || isUploadingBulk ? "bg-gray-300 text-white" : "bg-purple-600 text-white hover:bg-purple-700"
                      }`}
                      title={`Dosya adları 1.png, 2.mp4 şeklinde olmalıdır. Maks: ${questions.length} dosya`}>
                      <Upload className="w-4 h-4" />
                      {isUploadingBulk ? "Yükleniyor..." : "Toplu Yükle"}
                    </span>
                  </label>

                  <a
                    href="https://dokumantasyon.yeniegitimsistemi.net/ogretmen-rehberi/e-egitim/testlere-toplu-soru-ve-cevap-anahtari-nasil-yuklenir.html#6-toplu-soru-yükleme"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-purple-700 hover:bg-purple-100 rounded-lg"
                    title="Toplu Yükleme Rehberi"
                    aria-label="Toplu Yükleme Rehberi">
                    <Info className="w-4 h-4" />
                  </a>

                  <label className="relative">
                    <input
                      type="file"
                      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={handleAnswerKeySelected}
                      className="hidden"
                      disabled={loading || isUploadingAnswerKey}
                    />
                    <span
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm cursor-pointer ${
                        loading || isUploadingAnswerKey ? "bg-gray-300 text-white" : "bg-green-600 text-white hover:bg-green-700"
                      }`}
                      title="Excel: 1. sütun soru no, 2. sütun cevap (A,B,C,...)">
                      <FileSpreadsheet className="w-4 h-4" />
                      {isUploadingAnswerKey ? "Yükleniyor..." : "Cevap Anahtarı"}
                    </span>
                  </label>

                  <a
                    href="https://dokumantasyon.yeniegitimsistemi.net/ogretmen-rehberi/e-egitim/testlere-toplu-soru-ve-cevap-anahtari-nasil-yuklenir.html#7-cevap-anahtarı-yükleme"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-green-700 hover:bg-green-100 rounded-lg"
                    title="Cevap Anahtarı Rehberi"
                    aria-label="Cevap Anahtarı Rehberi">
                    <Info className="w-4 h-4" />
                  </a>

                  <button
                    onClick={handleCreateSingleQuestion}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    disabled={loading}>
                    <Plus className="w-4 h-4" />
                    Yeni Soru Ekle
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Sorular yükleniyor...</p>
                </div>
              ) : questions.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">Henüz hiç soru eklenmemiş.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {questions.map((question, index) => (
                    <div key={question.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-4 bg-gray-50 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                              <span className="text-sm font-semibold text-blue-600">{index + 1}</span>
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">Soru {index + 1}</h4>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    question.type === "TEXT" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"
                                  }`}>
                                  {question.type === "TEXT" ? (
                                    <>
                                      <FileText className="w-3 h-3 mr-1" />
                                      Metin
                                    </>
                                  ) : (
                                    <>
                                      <ImageIcon className="w-3 h-3 mr-1" />
                                      Resim
                                    </>
                                  )}
                                </span>
                                {/* Çözüm Videosu Badge */}
                                {((question.metadata as any)?.solutionVideoUrl || question.solutionMedia?.url) && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <Video className="w-3 h-3 mr-1" />
                                    Çözüm Videosu
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleQuestionExpansion(question.id)}
                              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                              {expandedQuestions[question.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => {
                                if (previewUrl) {
                                  URL.revokeObjectURL(previewUrl);
                                  setPreviewUrl(null);
                                }
                                setFile(null);
                                setEditingQuestion({
                                  ...question,
                                });
                                if ((question.metadata as any)?.solutionVideoUrl) {
                                  setSolutionType("vimeo");
                                  setSolutionVideoUrl((question.metadata as any).solutionVideoUrl);
                                  setSolutionVideoFile(null);
                                } else if (question.solutionMedia?.url) {
                                  setSolutionType("file");
                                  setSolutionVideoUrl("");
                                  setSolutionVideoFile(null);
                                } else {
                                  setSolutionType("vimeo");
                                  setSolutionVideoUrl("");
                                  setSolutionVideoFile(null);
                                }
                              }}
                              className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Düzenle">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteQuestion(question.id, question.content)}
                              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                              title="Sil">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {expandedQuestions[question.id] && (
                        <div className="p-4">
                          <div className="space-y-4">
                            {question.content && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Soru Başlığı</label>
                                <div className="p-3 bg-gray-50 rounded-lg border">
                                  <p className="text-gray-900">{question.content}</p>
                                </div>
                              </div>
                            )}

                            {question.type === "IMAGE" && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Soru Resmi</label>
                                {question.media && (
                                  <img
                                    src={question.media.url}
                                    alt="Soru resmi"
                                    className="max-w-md max-h-64 object-contain rounded-lg border"
                                  />
                                )}
                              </div>
                            )}

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Seçenekler</label>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {question.options.map((option, optIndex) => (
                                  <div
                                    key={optIndex}
                                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                                      question.correctOption === option ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"
                                    }`}>
                                    <div
                                      className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                                        question.correctOption === option ? "bg-green-500 text-white" : "bg-gray-300 text-gray-700"
                                      }`}>
                                      {String.fromCharCode(65 + optIndex)}
                                    </div>
                                    <span className="text-gray-900">{option}</span>
                                    {question.correctOption === option && (
                                      <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                        Doğru Cevap
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Çözüm Videosu Görüntüleme */}
                            {((question.metadata as any)?.solutionVideoUrl || question.solutionMedia?.url) && (
                              <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 text-sm">
                                    <Video className="w-4 h-4 text-gray-500" />
                                    <span className="text-gray-600">Çözüm Videosu:</span>
                                    {(question.metadata as any)?.solutionVideoUrl ? (
                                      <a
                                        href={(question.metadata as any).solutionVideoUrl.replace(/^@/, '')}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-700 hover:underline truncate max-w-xs">
                                        {(question.metadata as any).solutionVideoUrl.replace(/^@/, '')}
                                      </a>
                                    ) : question.solutionMedia?.url ? (
                                      <a
                                        href={question.solutionMedia.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-700 hover:underline truncate max-w-xs">
                                        {question.solutionMedia.name || "Video Dosyası"}
                                      </a>
                                    ) : null}
                                  </div>
                                  <button
                                    onClick={async () => {
                                      const confirmed = await showConfirm({
                                        title: "Çözüm Videosunu Sil",
                                        message: `Bu sorunun çözüm videosunu silmek istediğinizden emin misiniz?`,
                                        confirmText: "Sil",
                                        confirmButtonVariant: "danger",
                                      });
                                      if (!confirmed) return;
                                      try {
                                        await ApiService.deleteLessonSectionContentQuestionSolutionVideo(
                                          lessonId,
                                          sectionId,
                                          contentId,
                                          question.id
                                        );
                                        await fetchQuestions();
                                        await showAlert({
                                          title: "Başarılı",
                                          message: "Çözüm videosu başarıyla silindi.",
                                          type: "success",
                                        });
                                      } catch (error) {
                                        await showAlert({
                                          title: "Hata",
                                          message: "Çözüm videosu silinirken bir hata oluştu.",
                                          type: "error",
                                        });
                                      }
                                    }}
                                    className="flex items-center gap-1 px-2 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                    title="Çözüm Videosunu Sil">
                                    <Trash2 className="w-4 h-4" />
                                    Sil
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {editingQuestion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 bg-blue-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Edit className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Soruyu Düzenle</h3>
                      <p className="text-sm text-gray-600">Soru {questions.findIndex((q) => q.id === editingQuestion.id) + 1}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setEditingQuestion(null);
                      setFile(null);
                      if (previewUrl) {
                        URL.revokeObjectURL(previewUrl);
                        setPreviewUrl(null);
                      }
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Soru Türü</label>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() =>
                        setEditingQuestion({
                          ...editingQuestion,
                          type: "TEXT",
                          content: editingQuestion.content || "",
                          metadata: {
                            ...editingQuestion.metadata,
                            imageDataUrl: undefined,
                          },
                        })
                      }
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                        editingQuestion.type === "TEXT"
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                      }`}>
                      <FileText className="w-4 h-4" />
                      Metin Sorusu
                    </button>
                    <button
                      onClick={() =>
                        setEditingQuestion({
                          ...editingQuestion,
                          type: "IMAGE",
                          content: "",
                        })
                      }
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                        editingQuestion.type === "IMAGE"
                          ? "border-purple-500 bg-purple-50 text-purple-700"
                          : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                      }`}>
                      <ImageIcon className="w-4 h-4" />
                      Resim Sorusu
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Soru Başlığı <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={editingQuestion.content || ""}
                    onChange={(e) =>
                      setEditingQuestion({
                        ...editingQuestion,
                        content: e.target.value,
                      })
                    }
                    placeholder="Soru başlığını yazın..."
                    rows={4}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>

                {editingQuestion.type === "IMAGE" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Soru Resmi <span className="text-red-500">*</span>
                    </label>

                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 hover:bg-gray-100 transition-colors">
                      <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" id="image-upload" />
                      <label htmlFor="image-upload" className="cursor-pointer flex flex-col items-center justify-center">
                        {previewUrl || editingQuestion.media?.url ? (
                          <div className="mb-2">
                            <img
                              src={previewUrl || editingQuestion.media?.url}
                              alt="Soru resmi"
                              className="max-w-md max-h-64 object-contain rounded-lg border"
                            />
                            <p className="text-xs text-gray-500 text-center mt-2">
                              {previewUrl ? "Yeni Seçilen Resim" : "Mevcut Resim - Değiştirmek İçin Yeni Resim Seçin"}
                            </p>
                            {file && <p className="text-xs text-gray-500 text-center mt-1">Dosya: {file.name}</p>}
                          </div>
                        ) : (
                          <>
                            <Upload className="w-8 h-8 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600 text-center">
                              <span className="font-medium text-blue-600 hover:text-blue-700">Resim Seçmek İçin Tıklayın</span>
                              <br />
                              PNG, JPG, JPEG (Max 5MB)
                            </p>
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Seçenekler <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-3">
                    {editingQuestion.options.map((option, optIndex) => (
                      <div
                        key={optIndex}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-colors ${
                          editingQuestion.correctOption === option ? "border-green-300 bg-green-50" : "border-gray-200 bg-white"
                        }`}>
                        <input
                          type="radio"
                          name="editCorrectOption"
                          checked={editingQuestion.correctOption === option}
                          onChange={() =>
                            setEditingQuestion({
                              ...editingQuestion,
                              correctOption: option,
                            })
                          }
                          className="w-4 h-4 text-green-600"
                        />
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <span className="text-sm font-semibold text-blue-600">{String.fromCharCode(65 + optIndex)}</span>
                        </div>
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => handleOptionChange(optIndex, e.target.value, true)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          placeholder={`${String.fromCharCode(65 + optIndex)} seçeneği`}
                        />
                        {editingQuestion.options.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeOption(optIndex, true)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => addOption(true)}
                      className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-gray-700 transition-colors border-2 border-dashed border-gray-300 hover:border-gray-400 flex items-center justify-center gap-2">
                      <Plus className="w-4 h-4" />
                      Seçenek Ekle
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Çözüm Videosu
                    <span className="text-gray-500 text-xs ml-1">(İsteğe bağlı)</span>
                  </label>

                  {/* Mevcut Çözüm Videosu - Tipine bakılmaksızın göster */}
                  {((editingQuestion?.metadata as any)?.solutionVideoUrl || editingQuestion?.solutionMedia?.url) && (
                    <div className="p-3 bg-white rounded-lg border border-gray-200 mb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm">
                          <Video className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-600">Mevcut:</span>
                          {(editingQuestion?.metadata as any)?.solutionVideoUrl ? (
                            <a
                              href={(editingQuestion?.metadata as any).solutionVideoUrl.replace(/^@/, '')}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 hover:underline truncate max-w-xs">
                              {(editingQuestion?.metadata as any).solutionVideoUrl.replace(/^@/, '')}
                            </a>
                          ) : editingQuestion?.solutionMedia?.url ? (
                            <a
                              href={editingQuestion.solutionMedia.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 hover:underline truncate max-w-xs">
                              {editingQuestion.solutionMedia.name || "Video Dosyası"}
                            </a>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            const confirmed = await showConfirm({
                              title: "Çözüm Videosunu Sil",
                              message: `Bu sorunun çözüm videosunu silmek istediğinizden emin misiniz?`,
                              confirmText: "Sil",
                              confirmButtonVariant: "danger",
                            });
                            if (!confirmed) return;
                            try {
                              await ApiService.deleteLessonSectionContentQuestionSolutionVideo(
                                lessonId,
                                sectionId,
                                contentId,
                                editingQuestion.id
                              );
                              await fetchQuestions();
                              setEditingQuestion(null);
                              setSolutionVideoUrl("");
                              setSolutionVideoFile(null);
                              await showAlert({
                                title: "Başarılı",
                                message: "Çözüm videosu başarıyla silindi.",
                                type: "success",
                              });
                            } catch (error) {
                              await showAlert({
                                title: "Hata",
                                message: "Çözüm videosu silinirken bir hata oluştu.",
                                type: "error",
                              });
                            }
                          }}
                          className="flex items-center gap-1 px-2 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors">
                          <Trash2 className="w-4 h-4" />
                          Sil
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Yeni Çözüm Videosu Ekleme Sekmeleri */}
                  <div className="flex gap-3 mb-3">
                    <button
                      type="button"
                      onClick={() => setSolutionType("vimeo")}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                        solutionType === "vimeo"
                          ? "bg-blue-50 border-blue-500 text-blue-700"
                          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}>
                      <FileText className="w-4 h-4" />
                      Vimeo Linki
                    </button>
                    <button
                      type="button"
                      onClick={() => setSolutionType("file")}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                        solutionType === "file"
                          ? "bg-green-50 border-green-500 text-green-700"
                          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}>
                      <Video className="w-4 h-4" />
                      Dosya Yükle
                    </button>
                  </div>

                  {solutionType === "vimeo" && (
                    <div>
                      <input
                        type="url"
                        value={solutionVideoUrl}
                        onChange={(e) => setSolutionVideoUrl(e.target.value)}
                        placeholder="https://vimeo.com/123456789"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      {((editingQuestion?.metadata as any)?.solutionVideoUrl || editingQuestion?.solutionMedia?.url) && (
                        <div className="mt-1 text-xs text-gray-500">
                          Yeni link girildiğinde mevcut çözüm videosu değiştirilecektir.
                        </div>
                      )}
                    </div>
                  )}

                  {solutionType === "file" && (
                    <div className="space-y-3">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-white hover:bg-gray-50 transition-colors">
                        <input
                          type="file"
                          accept="video/*"
                          onChange={handleSolutionVideoFileChange}
                          className="hidden"
                          id="solution-video-upload"
                        />
                        <label htmlFor="solution-video-upload" className="cursor-pointer flex flex-col items-center justify-center">
                          {solutionVideoFile ? (
                            <div className="w-full">
                              <div className="flex items-center justify-center gap-2">
                                <Video className="w-5 h-5 text-green-600" />
                                <span className="text-sm font-medium text-green-700">{solutionVideoFile.name}</span>
                                <span className="text-xs text-green-600">({(solutionVideoFile.size / 1024 / 1024).toFixed(1)} MB)</span>
                              </div>
                              <div className="mt-2 text-center">
                                <span className="text-xs text-gray-500">Farklı bir video seçmek için tıklayın</span>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center">
                              <Video className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                              <span className="text-sm font-medium text-gray-700">Video yüklemek için tıklayın</span>
                              <p className="text-xs text-gray-500 mt-1">MP4, AVI, MOV (Max: 100MB)</p>
                            </div>
                          )}
                        </label>
                      </div>

                      {solutionVideoFile && (
                        <div className="flex justify-between items-center">
                          <div className="text-xs text-gray-500">
                            Yeni dosya yüklendiğinde mevcut çözüm videosu değiştirilecektir.
                          </div>
                          <button
                            type="button"
                            onClick={() => setSolutionVideoFile(null)}
                            className="text-sm text-red-600 hover:text-red-700">
                            Seçimi Kaldır
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setEditingQuestion(null);
                      setFile(null);
                      setSolutionVideoUrl("");
                      setSolutionVideoFile(null);
                      setSolutionType("vimeo");
                      if (previewUrl) {
                        URL.revokeObjectURL(previewUrl);
                        setPreviewUrl(null);
                      }
                    }}
                    className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors">
                    İptal
                  </button>
                  <button
                    onClick={handleUpdateQuestion}
                    className="px-8 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    Güncelle
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
